import {gsap} from "gsap";
import { DrawSVGPlugin } from "gsap/DrawSVGPlugin";
import * as DownloadJS from 'downloadjs';
import html2canvas from 'html2canvas';

gsap.registerPlugin(DrawSVGPlugin);

const CLASS_ACTIVE = 'is-active';
const CLASS_DISABLED = 'is-disabled';
const KEY_LOCALSTORAGE = 'tt_data';
const KEY_LOCALFLAGS = 'tt_flags';
const PageTemplate = document.querySelector('template#page-template');
const IsSignedIn = !!window.PageSignedIn;
const WarframeThumbnails = window.WarframeThumbnails;
const PageButtons = window.PageButtons || {};
const ImageRoot = window.ImageRoot + '/';
const MediaRoot = window.MediaRoot + '/';
const DataEndpoint = window.DataEndpoint;
const SimulatorError = document.querySelector('.SimulatorError');
const SimulatorHeader = document.querySelector('.SimulatorHeader');
const SimulatorRoot = document.querySelector('.Simulator');
const SimulatorBackground = document.querySelector('.SimulatorBackground');
const SimulatorBackgroundLines = SimulatorBackground.querySelector('.SimulatorBackground-lines');
const SimulatorBackgroundGlow = SimulatorBackground.querySelector('.SimulatorBackground-glow');
const SimulatorLogo = SimulatorBackground.querySelector('.SimulatorLogo');
const SimulatorRootElement = document.querySelector('.Simulator');
const SimulatorContentElement = SimulatorRootElement.querySelector('.Simulator-content');
let currentPageElement = null;
let currentGlitchPage = null;
let stateData = null;
let stateFlags = null;
let lockUI = true;
let interacted = false;
let interactionCallback = null;
let textInterval = null;

// Local storage check
function localStorageCheck() {
    if (!localStorage) return false;
    try {
        localStorage.setItem('test', 'test');
        localStorage.removeItem('test');
        return true;
    } catch {
        return false;
    }
}
const LocalStorageAvailable = localStorageCheck();

function saveStateData() {
    if (LocalStorageAvailable) {
        localStorage.setItem(KEY_LOCALSTORAGE, JSON.stringify(stateData));
        localStorage.setItem(KEY_LOCALFLAGS, JSON.stringify(stateFlags));
    }
}
function trySetFlag(index) {
    if (index < 0 || index > 4) return;
    if (stateFlags == null || stateFlags.length !== 5) {
        stateFlags = [0,0,0,0,0];
    }
    stateFlags[index] = 1;
    saveStateData();
    updateDebugText();
}

function checkFlags() {
    if (stateFlags == null || stateFlags.length !== 5) return false;
    return stateFlags[0] && stateFlags[1] && stateFlags[2] && stateFlags[3] && stateFlags[4];
}

// Last index from local storage
if (LocalStorageAvailable) {
    const raw = localStorage.getItem(KEY_LOCALSTORAGE);
    if (raw) {
        try {
            const json = JSON.parse(raw);
            if (json) {
                stateData = json;
            }
        } catch (e) {
            console.warn('Could not parse local storage data: ' + raw);
        }
    } else {
        resetStateData();
    }
    const rawFlags = localStorage.getItem(KEY_LOCALFLAGS);
    if (rawFlags) {
        try {
            const json = JSON.parse(rawFlags);
            if (json) {
                stateFlags = json;
            }
        } catch (e) {
            stateFlags = null;
        }
    } else {
        stateFlags = null;
    }
} else {
    resetStateData();
}

function resetStateData() {
    stateData = {
        page: 1,
        frame: 0,
        alignment: [0,0,0,0,0],
    };
}

const pageElements = {};
const pageData = {};
const pageAudio = {};
const pageAmbient = {};
const glitchElements = {};

function integratePageData(newData, forced=false) {
    if (newData && newData.length) {
        for(const page of newData) {
            if (page.id) {
                pageData[page.id] = page;
                if (forced) {
                    const old = pageElements[page.id];
                    pageElements[page.id] = createPage(page);
                    if (old) {
                        old.replaceWith(pageElements[page.id]);
                    } else {
                        SimulatorContentElement.appendChild(pageElements[page.id]);
                    }
                }
            } else {
                console.error('Incoming page data missing id', page);
            }
        }
    }
}

integratePageData(window.PageData);

function resetSim() {
    hideGlitchPage();
    resetStateData();
    showCurrentPage();
    saveStateData();
    setStage(1);
}
function showPage(index) {
    stopCurrentAudio();
    if (pageData[index] && !pageData[index].volatile) {
        stateData.page = index;
        hideGlitchPage();
        showCurrentPage();
        saveStateData();
    } else {
        getPageData(index).then(() => {
            if (pageData[index]) {
                stateData.page = index;
                showCurrentPage();
                saveStateData();
            } else {
                console.error('Error: unable to retrieve requested page index ' + index);
            }
        });
    }
}
async function getPageData(index) {
    const formData = new FormData();
    formData.append('_token', DataEndpoint.c);
    formData.append('page', index);
    const response = await fetch(
        DataEndpoint.route,
        {
            method: 'POST',
            body: formData,
        }
    );
    const data = await response.json();
    if (data.success) {
        integratePageData(data.pages, true);
    } else {
        SimulatorError.classList.add(CLASS_ACTIVE);
    }
}
async function setWarframe(stage, frame) {
    stateData.frame = frame;
    const formData = new FormData();
    formData.append('_token', DataEndpoint.c);
    formData.append('stage', stage);
    formData.append('warframe', frame);
    const response = await fetch(
        DataEndpoint.route,
        {
            method: 'POST',
            body: formData,
        }
    );
    await response.json();
}
async function setStage(index, flags = null) {
    const formData = new FormData();
    formData.append('_token', DataEndpoint.c);
    formData.append('stage', index);
    if (flags) {
        formData.append('flags', flags[0] + flags[1] + flags[2] + flags[3] + flags[4]);
    }
    const response = await fetch(
        DataEndpoint.route,
        {
            method: 'POST',
            body: formData,
        }
    );
    await response.json();
}

function hideCurrentPage() {
    if (currentPageElement) {
        currentPageElement.classList.remove(CLASS_ACTIVE);
    }
}
function showCurrentPage(audio=true) {
    const data = pageData[stateData.page];
    if (!data) return;

    clearInterval(textInterval);
    interactionCallback = null;
    hideCurrentPage();
    if (!pageElements[stateData.page]) {
        pageElements[stateData.page] = createPage(data);
        SimulatorContentElement.appendChild(pageElements[stateData.page]);
    }
    if (pageElements[stateData.page]) {
        currentPageElement = pageElements[stateData.page];
        clearSelections(currentPageElement);
        currentPageElement.classList.add(CLASS_ACTIVE);
        if (data.scoreCard) {
            updateScoreCard(currentPageElement);
        }
    }
    SimulatorLogo.setAttribute('data-state', data.logo || 3);
    if (audio) {
        playAudio(data.audio ? pageAudio[data.audio] : null, data.audioDelay);
    }
    playAmbient(data.ambient, data.ambientDelay);

    if (data.card.animatedText) {
        let delay = data.audioDelay || 0;
        if (data.textDelay) delay += data.textDelay;
        animateText(currentPageElement, delay);
    }

    lockUI = false;
    updateDebugText();
}
function clearSelections(element) {
    const selections = element.querySelectorAll('.is-selected');
    selections && selections.forEach((selection) => {
       selection.classList.remove('is-selected');
    });
}

let currentAudioTrack = null;
let previousAmbientTrack = null;
let currentAmbientTrack = null;
let currentAmbientKey = null;
let allowAudio = true;
let ambientDelayTicks = 0;

function tryPlayAudio(key, reset=false) {
    if (interacted && allowAudio && pageAudio[key]) {
        try {
            if (reset && pageAudio[key].currentTime > 0) pageAudio[key].currentTime = 0;
            if (pageAudio[key].paused) pageAudio[key].play();
        } catch (e) {
            // do nothing
        }
    }
}
function playAmbient(key, reset=false, ambientDelay = 0) {
    if (!key) key = 'default';

    if (pageAmbient[key]) {
        if (currentAmbientKey === key) return;

        if (previousAmbientTrack && !previousAmbientTrack.paused) {
            try {
                previousAmbientTrack.pause();
            } catch (e) {
                // Do nothing
            }
        }

        previousAmbientTrack = currentAmbientTrack;
        currentAmbientTrack = pageAmbient[key];
        currentAmbientKey = key;
        if (interacted && allowAudio) {
            playCurrentAmbient(reset);
        }
        if (ambientDelay) {
            ambientDelayTicks = SOUND_TICKS * ambientDelay;
        }
    } else {
        previousAmbientTrack = currentAmbientTrack;
        currentAmbientTrack = null;
        currentAmbientKey = null;
    }
}
function playAudio(audio = null, audioDelay = 0) {
    stopCurrentAudio();
    currentAudioTrack = audio;
    if (currentAudioTrack) currentAudioTrack.currentTime = 0;
    if (interacted && allowAudio) {
        if (audioDelay) {
            setTimeout(playCurrentAudio, audioDelay * 1000);
        } else {
            playCurrentAudio();
        }
    }
}
function playCurrentAudio(reset=false) {
    try {
        if (currentAudioTrack) {
            if (reset) currentAudioTrack.currentTime = 0;
            if (currentAudioTrack.paused) {
                currentAudioTrack.play();
            }
        }
    } catch (e) {
        // do nothing
    }
}
function playCurrentAmbient(reset=false) {
    try {
        if (currentAmbientTrack) {
            if (reset) currentAmbientTrack.currentTime = 0;
            if (currentAmbientTrack.paused) {
                currentAmbientTrack.play();
            }
        }
    } catch (e) {
        // do nothing
    }
}
function stopAllAudio(reset=false) {
    stopCurrentAudio(reset);
    stopCurrentAmbient(reset);
}
function stopCurrentAudio(reset=false) {
    try {
        if (currentAudioTrack && !currentAudioTrack.paused) {
            currentAudioTrack.pause();
            if (reset) currentAudioTrack.currentTime = 0;
        }
    } catch (e) {
        // do nothing
    }
}
function stopCurrentAmbient(reset=false) {
    try {
        if (currentAmbientTrack && !currentAmbientTrack.paused) {
            currentAmbientTrack.pause();
            if (reset) currentAmbientTrack.currentTime = 0;
        }
    } catch (e) {
        // do nothing
    }
}

function unmuteAudio() {
    allowAudio = true;
    playCurrentAudio();
    playCurrentAmbient();
}
function muteAudio() {
    allowAudio = false;
    stopAllAudio();
}
function createText({root, text, glitchText=null, animated=false}) {
    const element = document.createElement('p');
    let content = text;
    if (text.content) {
        if (text.classes) {
            for (const css of text.classes.split(' ')) {
                element.classList.add(css);
            }
        }
        content = text.content;
    }
    if (animated) {
        element.classList.add('AnimatedText');
        if (text.speed) element.dataset.speed = text.speed;

        const placeholder = document.createElement('span');
        placeholder.classList.add('AnimatedText-placeholder');
        placeholder.innerText = content;
        element.appendChild(placeholder);
        const inner = document.createElement('span');
        inner.classList.add('AnimatedText-inner');
        element.appendChild(inner);
        const visible = document.createElement('span');
        visible.classList.add('AnimatedText-visible');
        inner.appendChild(visible);
        const t1 = document.createElement('span');
        t1.classList.add('AnimatedText-transition1');
        inner.appendChild(t1);
        const t2 = document.createElement('span');
        t2.classList.add('AnimatedText-transition2');
        inner.appendChild(t2);
        const t3 = document.createElement('span');
        t3.classList.add('AnimatedText-transition3');
        inner.appendChild(t3);
        const rem = document.createElement('span');
        rem.classList.add('AnimatedText-remainder');
        inner.appendChild(rem);
        root.appendChild(element);
    } else {
        element.innerText = content;
        root.appendChild(element);
    }
    if (glitchText) {
        element.classList.add('SimulatorGlitchText');
        const alt = document.createElement('span');
        alt.classList.add('SimulatorGlitchText-alt');
        alt.innerText = glitchText;
        element.appendChild(alt);
        if (!animated) {
            element.classList.add(CLASS_ACTIVE);
        }
    }
}

function adjustAlignment(index) {
    if (index < 0 || index > 4) return;
    if (!stateData.alignment) {
        stateData.alignment = [0,0,0,0,0];
    }
    if (!stateData.alignment[index]) {
        stateData.alignment[index] = 0;
    }
    stateData.alignment[index] ++;
    saveStateData();
    updateDebugText();
}

let refreshLockout = 0;
let refreshInterval = null;

function createPage(pageData) {
    let wideActions = false;
    const page = PageTemplate.content.cloneNode(true);
    const pageElement = page.querySelector('.SimulatorPage');
    pageElement.dataset.id = pageData.id;
    const innerElement = page.querySelector('.SimulatorPage-inner');

    if (pageData.classes) {
        pageData.classes.forEach((c) => pageElement.classList.add(c));
        if (pageData.classes.includes('choice')) wideActions = true;
    }
    if (pageData.art) {
        const imgContainer = document.createElement('div');
        imgContainer.classList.add('SimulatorPage-art');
        const imgAccents = document.createElement('div');
        imgAccents.classList.add('SimulatorPage-artAccents');
        imgContainer.appendChild(imgAccents);
        const img = document.createElement('img');
        img.classList.add('SimulatorPage-image');
        img.src = ImageRoot + pageData.art;
        img.alt = '';
        imgContainer.appendChild(img);

        if (pageData.glitch) {
            const value = pageData.glitch.index;
            createGlitchPage(pageData.id, pageData.glitch);

            const glitchImg = document.createElement('div');
            glitchImg.classList.add('SimulatorImageGlitch');
            glitchImg.setAttribute('data-index', value);
            if (pageData.glitch.overlay) {
                glitchImg.style.backgroundImage = 'url(' + ImageRoot + pageData.glitch.overlay + ')';
            }
            glitchImg.addEventListener('click', (e) => {
                e.stopPropagation();
                interacted = true;

                trySetFlag(value);
                stopCurrentAudio();
                showGlitchPage(value);
                //tryPlayAudio('glitch');

                window.scrollTo(0, 0);
            });

            imgContainer.appendChild(glitchImg);
        }

        innerElement.appendChild(imgContainer);
    }
    /*
    if (pageData.progress) {
        const progressElement = document.createElement('div');
        progressElement.classList.add('SimulatorPageProgress');
        pageElement.appendChild(progressElement);

        for (let index = 1; index <= 8; index++) {
            if (index > 1) {
                const divider = document.createElement('div');
                divider.classList.add('SimulatorPageProgress-divider');
                progressElement.appendChild(divider);
            }
            const node = document.createElement('div');
            node.classList.add('SimulatorPageProgress-node');
            if (pageData.progress === index) node.classList.add(CLASS_ACTIVE);
            progressElement.appendChild(node);
        }
    }
    */
    if (pageData.card) {
        const cardElement = document.createElement('div');
        cardElement.classList.add('SimulatorPage-card');
        if (pageData.card.background) {
            cardElement.style.backgroundImage = 'url(' + ImageRoot + pageData.card.background + ')';
        }
        innerElement.appendChild(cardElement);

        if (pageData.card.challenge || pageData.classes && pageData.classes.includes('challengeComplete')) {
            const spacer = document.createElement('div');
            spacer.classList.add('SimulatorPage-badgeSpacer');
            cardElement.appendChild(spacer);
            const element = document.createElement('div');
            element.classList.add('SimulatorPage-badgeIcon');
            cardElement.appendChild(element);

            const image = document.createElement('img');
            image.classList.add('SimulatorPage-badgeImage');
            if (pageData.card.challenge && !pageData.card.challenge.completed) {
                image.src = ImageRoot + '/icon-flag.svg';
            } else {
                image.src = ImageRoot + '/icon-check.svg';
            }
            image.alt = '';
            element.appendChild(image);
        }
        if (pageData.card.title) {
            const element = document.createElement('div');
            element.classList.add('SimulatorPage-title');
            element.innerText = pageData.card.title;
            cardElement.appendChild(element);
        }
        if (pageData.card.text) {
            const text = document.createElement('div');
            text.classList.add('SimulatorPage-text');
            pageData.card.text.forEach((t) => {
                createText({
                    root: text,
                    text: t,
                    animated: pageData.card.animatedText,
                });

                if (t.classes === 'challenge' && pageData.card.challenge) {
                    const amount = Math.min(pageData.card.challenge.progress, pageData.card.challenge.target);
                    const progressText = `${amount}/${pageData.card.challenge.target}`;
                    const percent = Math.min(100.0, 100.0 * (pageData.card.challenge.progress / pageData.card.challenge.target));

                    const progressBar = document.createElement('div');
                    progressBar.classList.add('ChallengeProgress');
                    text.appendChild(progressBar);

                    const barText = document.createElement('div');
                    barText.classList.add('ChallengeProgress-barText');
                    barText.innerText = progressText;
                    progressBar.appendChild(barText);

                    const bar = document.createElement('div')
                    bar.classList.add('ChallengeProgress-bar');
                    //bar.style.width = (100.0 * (pageData.card.challenge.progress / pageData.card.challenge.target)) + '%';
                    bar.style.maskImage = `linear-gradient(to right, black ${percent}%, transparent ${percent + 0.1}%)`;
                    progressBar.appendChild(bar);

                    const barInvText = document.createElement('div');
                    barInvText.classList.add('ChallengeProgress-barText');
                    barInvText.classList.add('ChallengeProgress-barTextInv');
                    barInvText.innerText = progressText;
                    bar.appendChild(barInvText);
                }
            });
            cardElement.appendChild(text);

            if (pageData.card.glitchText) {
                createText({
                    root: text,
                    text: pageData.card.glitchText.original,
                    glitchText: pageData.card.glitchText.alternate,
                    animated: pageData.card.animatedText,
                });
            }
        }
        if (pageData.scoreCard) {
            const accents = document.createElement('div');
            accents.classList.add('ReportCardAccents');
            cardElement.appendChild(accents);
            const score = document.createElement('div');
            score.classList.add('SimulatorPage-score');
            score.id = 'score-card';
            cardElement.appendChild(score);
        }
        if (pageData.card.challenge) {
            const actions = document.createElement('div');
            actions.classList.add('SimulatorPage-actions');
            cardElement.appendChild(actions);

            const button = document.createElement('div');
            button.classList.add('Button');
            button.innerText = pageData.card.challenge.button;
            actions.appendChild(button);

            const bracket = document.createElement('div');
            bracket.classList.add('SimulatorButtonBracket');
            button.appendChild(bracket);

            if (pageData.card.challenge.completed) {
                button.classList.add('Button--tau');
                button.addEventListener('mouseover', () => {
                    tryPlayAudio('hover', true);
                });
                button.addEventListener('click', (e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    interacted = true;

                    if (lockUI) return;
                    lockUI = true;
                    tryPlayAudio('click');
                    button.classList.add('is-selected');

                    // Go to page
                    setTimeout(() => {

                        showPage(pageData.card.challenge.page);
                        if (pageData.card.challenge.alignment) {
                            adjustAlignment(pageData.card.challenge.alignment);
                        }
                        setStage(pageData.id);

                    }, 1000);
                });
            } else {
                button.classList.add('Button--incomplete');
                setStage(pageData.id);

                function refresh() {
                    const now = Date.now();
                    if (refreshLockout > now) {
                        refreshText.innerText = Math.ceil((refreshLockout - now) / 1000).toString();
                    } else {
                        refreshText.innerText = '';
                        clearInterval(refreshInterval);
                        refresher.classList.add(CLASS_ACTIVE);
                    }
                }

                const refresher = document.createElement('div');
                refresher.classList.add('ChallengeRefresh')
                if (refreshLockout > Date.now()) {
                    refreshInterval = setInterval(refresh, 1000);
                } else {
                    refresher.classList.add(CLASS_ACTIVE)
                }

                cardElement.appendChild(refresher);
                const refreshIcon = document.createElement('div');
                refreshIcon.classList.add('ChallengeRefresh-icon')
                refresher.appendChild(refreshIcon);
                const refreshText = document.createElement('div');
                refreshText.classList.add('ChallengeRefresh-text')
                refresher.appendChild(refreshText);

                refresher.addEventListener('mouseover', () => {
                    if (refresher.classList.contains(CLASS_ACTIVE)) {
                        tryPlayAudio('hover', true);
                    }
                });
                refresher.addEventListener('click', (e) => {
                    e.stopPropagation();
                    if (refresher.classList.contains(CLASS_ACTIVE)) {
                        refresher.classList.remove(CLASS_ACTIVE)
                        lockUI = true;
                        refreshLockout = Date.now() + 60 * 1000;
                        hideCurrentPage();
                        getPageData(stateData.page).then(() => {
                            showCurrentPage(false);
                        });
                    }
                });
            }
        }
        if (pageData.card.actions) {
            const actions = document.createElement('div');
            actions.classList.add('SimulatorPage-actions');
            pageData.card.actions.forEach((action) => {
                const button = document.createElement('div');
                button.classList.add('Button');
                button.classList.add('Button--tau');
                if (action.css) {
                    button.classList.add(action.css);
                }
                button.innerText = action.button;
                actions.appendChild(button);

                const bracket = document.createElement('div');
                bracket.classList.add('SimulatorButtonBracket');
                if (wideActions) {
                    bracket.classList.add('SimulatorButtonBracket--wide');
                }
                button.appendChild(bracket);

                if (action.disabled) {
                    button.classList.add(CLASS_DISABLED);
                } else {
                    button.addEventListener('mouseover', () => {
                        tryPlayAudio('hover', true);
                    });
                    button.addEventListener('click', (e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        interacted = true;

                        if (lockUI) return;

                        lockUI = true;

                        tryPlayAudio('click');

                        button.classList.add('is-selected');

                        setTimeout(() => {
                            if (action.reset) {
                                resetSim();
                                return;
                            }

                            // Progress incrementer
                            if (action.alignment) {
                                adjustAlignment(action.alignment);
                            }
                            // Forced page report
                            if (action.report) {
                                try {
                                    setStage(action.report);
                                } catch (e) {
                                    // do nothing
                                }
                            }
                            // Go to page
                            if (action.page) {
                                showPage(action.page);
                            }

                        }, 1000);
                    });
                }
            });
            cardElement.appendChild(actions);
        }
        if (pageData.card.warframes) {
            const warframes = document.createElement('div');
            warframes.classList.add('SimulatorPage-warframes');
            pageData.card.warframes.forEach((option) => {
                const warframe = document.createElement('div');
                warframe.classList.add('WarframeOption');
                if (option.frame) {
                    warframe.setAttribute('data-warframe', option.frame);
                }
                warframes.appendChild(warframe);

                const media = document.createElement('div');
                media.classList.add('WarframeOption-media');
                warframe.appendChild(media);

                const border = document.createElement('div');
                border.classList.add('WarframeOption-border');
                media.appendChild(border);
                const borderActive = document.createElement('div');
                borderActive.classList.add('WarframeOption-borderActive');
                media.appendChild(borderActive);

                const thumbnail = document.createElement('img');
                thumbnail.classList.add('WarframeOption-image');
                thumbnail.src = WarframeThumbnails[option.frame];
                thumbnail.alt = '';
                media.appendChild(thumbnail);
                const label = document.createElement('div');
                label.classList.add('WarframeOption-label');
                label.innerText = option.name;
                warframe.appendChild(label);
                const bracket = document.createElement('div');
                bracket.classList.add('WarframeOption-bracket');
                label.appendChild(bracket);

                warframe.addEventListener('mouseover', () => {
                    tryPlayAudio('hover', true);
                });
                warframe.addEventListener('click', (e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    interacted = true;

                    if (lockUI) return;
                    lockUI = true;

                    warframe.classList.add('is-selected');
                    tryPlayAudio('click');

                    setTimeout(() => {

                        // Select frame
                        if (option.frame) {
                            setWarframe(pageData.id, option.frame)
                            tryPlayAudio('frame' + option.frame);
                        }
                        // Go to page
                        if (option.page) {
                            showPage(option.page);
                        }

                    }, 1000);
                });
            });
            cardElement.appendChild(warframes);
        }
    }
    if (pageData.audio && !pageAudio[pageData.audio]) {
        pageAudio[pageData.audio] = new Audio(MediaRoot+pageData.audio+'.mp3');
    }
    if (pageData.ambient && !pageAmbient[pageData.ambient]) {
        pageAmbient[pageData.ambient] = new Audio(MediaRoot+pageData.ambient+'.mp3');
        pageAmbient[pageData.ambient].volume = 0;
        pageAmbient[pageData.ambient].loop = true;
    }
    return pageElement;
}
function animateText(rootElement, delay) {
    if (!rootElement) return;

    const elements = rootElement.querySelectorAll('.AnimatedText');
    if (elements && elements.length > 0) {
        const paragraphs = [];
        let skip = false;
        let p = 0;
        let paragraph, visible, trans1, trans2, trans3, remainder;
        let text, length, speed;

        elements.forEach((paragraph) => {
            paragraph.classList.remove(CLASS_ACTIVE);
            speed = parseInt(paragraph.getAttribute('data-speed') || 10);
            text = paragraph.querySelector('.AnimatedText-placeholder').innerText;
            length = text.length;
            visible = paragraph.querySelector('.AnimatedText-visible');
            trans1 = paragraph.querySelector('.AnimatedText-transition1');
            trans2 = paragraph.querySelector('.AnimatedText-transition2');
            trans3 = paragraph.querySelector('.AnimatedText-transition3');
            remainder = paragraph.querySelector('.AnimatedText-remainder');
            visible.innerText = '';
            trans1.innerText = '';
            trans2.innerText = '';
            trans3.innerText = '';
            remainder.innerText = '';
            paragraphs.push({
                speed,
                text,
                length,
                visible,
                trans1,
                trans2,
                trans3,
                remainder
            });
        });

        function skipText () {
            skip = true;
        }

        rootElement.addEventListener('click', skipText);
        interactionCallback = skipText;

        function initParagraph() {
            paragraph = elements[p];
            speed = paragraphs[p].speed;
            text = paragraphs[p].text;
            length = paragraphs[p].length;
            visible = paragraphs[p].visible;
            trans1 = paragraphs[p].trans1;
            trans2 = paragraphs[p].trans2;
            trans3 = paragraphs[p].trans3;
            remainder = paragraphs[p].remainder;
        }
        initParagraph();

        let textDelayTicks = delay * speed;
        textInterval = setInterval(() => {
            if (skip) {
                visible.innerText = paragraphs[p].text;
                trans1.innerText = '';
                trans2.innerText = '';
                trans3.innerText = '';
                remainder.innerText = '';
                if (paragraph.classList.contains('SimulatorGlitchText')) {
                    paragraph.classList.add(CLASS_ACTIVE);
                }
                p++;

                while (p < paragraphs.length) {
                    paragraphs[p].visible.innerText = paragraphs[p].text;
                    if (elements[p].classList.contains('SimulatorGlitchText')) {
                        elements[p].classList.add(CLASS_ACTIVE);
                    }
                    p++;
                }
                clearInterval(textInterval);
                interactionCallback = null;
                return;
            }
            if (textDelayTicks > 0) {
                textDelayTicks--;
                return;
            }

            if (visible.innerText.length < length) {
                visible.innerText += trans1.innerText;
                trans1.innerText = trans2.innerText;
                trans2.innerText = trans3.innerText;
                if (text.length > 0) {
                    trans3.innerText = text.charAt(0);
                    text = text.substring(1);
                } else {
                    trans3.innerText = '';
                }
                remainder.innerText = text;
            } else {
                paragraph.classList.add(CLASS_ACTIVE);
                p ++;
                if (p < elements.length) {
                    initParagraph();
                } else {
                    clearInterval(textInterval);
                    interactionCallback = null;
                }
            }

        }, 1000/speed)
    }
}

function updateDebugText() {
    if (IsSignedIn && window.outputDebugText) {
        window.outputDebugText(stateData, stateFlags);
    }
}

async function updateScoreCard(element) {
    const root = element.querySelector('#score-card');
    root.innerHTML = '';
    const loader = document.createElement('div');
    loader.classList.add('ReportCardLoader');
    root.appendChild(loader);

    const formData = new FormData();
    formData.append('_token', DataEndpoint.c);
    const response = await fetch(
        DataEndpoint.score,
        {
            method: 'POST',
            body: formData,
        }
    );
    const data = await response.json();
    if (data.success) {
        root.innerHTML = data.content;
    } else {
        root.innerHTML = '';
        const error = document.createElement('div');
        error.classList.add('ReportCardError');
        error.innerText = data.content;
        root.appendChild(error);
    }
    if (Chart) {
        if (data.warframes) {
            const WarframeCanvas = root.querySelector("canvas.ReportCardWarframe-chart");
            const WarframeContext = WarframeCanvas.getContext("2d");
            const RhinoGradient = WarframeContext.createLinearGradient(0, 0, 0, 420);
            RhinoGradient.addColorStop(0, 'rgba(26, 62, 150, 0.5)');
            RhinoGradient.addColorStop(1, 'rgba(26, 62, 150, 0)');
            const IvaraGradient = WarframeContext.createLinearGradient(0, 0, 0, 420);
            IvaraGradient.addColorStop(0, 'rgba(175, 119, 91, 0.5)');
            IvaraGradient.addColorStop(1, 'rgba(175, 119, 91, 0)');
            const WispGradient = WarframeContext.createLinearGradient(0, 0, 0, 420);
            WispGradient.addColorStop(0, 'rgba(155, 53, 56, 0.5)');
            WispGradient.addColorStop(1, 'rgba(155, 53, 56, 0)');
            const KhoraGradient = WarframeContext.createLinearGradient(0, 0, 0, 420);
            KhoraGradient.addColorStop(0, 'rgba(6, 89, 96, 0.5)');
            KhoraGradient.addColorStop(1, 'rgba(6, 89, 96, 0)');
            const NekrosGradient = WarframeContext.createLinearGradient(0, 0, 0, 420);
            NekrosGradient.addColorStop(0, 'rgba(141, 102, 154, 0.5)');
            NekrosGradient.addColorStop(1, 'rgba(141, 102, 154, 0)');
            data.warframes.datasets[0].backgroundColor = [
                RhinoGradient,
                IvaraGradient,
                WispGradient,
                KhoraGradient,
                NekrosGradient,
            ];
            data.warframes.datasets[0].borderColor = [
                'rgba(26, 62, 150, 1)',
                'rgba(175, 119, 91, 1)',
                'rgba(155, 53, 56, 1)',
                'rgba(6, 89, 96, 1)',
                'rgba(141, 102, 154, 1)',
            ];
            data.warframes.datasets[0].borderWidth = 3;
            new Chart(
                WarframeCanvas,
                {
                    type: 'bar',
                    data: data.warframes,
                    options: {
                        maintainAspectRatio: false,
                        scales: {
                            x: {
                                grid: {
                                    color: 'transparent',
                                },
                            },
                            y: {
                                beginAtZero: true,
                                ticks: {
                                    callback: (value, index, ticks) => {
                                        return (100.0 * value).toFixed(0) + '%';
                                    }
                                },
                                grid: {
                                    color: 'rgba(255,255,255,.1)',
                                },
                                border: {
                                    dash: [20, 10, 80, 10],
                                }
                            },
                        },
                        plugins: {
                            legend: {
                                display: false,
                            },
                            tooltip: {
                                callbacks: {
                                    label: (context) => {
                                        let label = context.dataset.label || '';
                                        if (label) label += ': ';
                                        label += (100.0 * context.parsed.y).toFixed(1) + '%';
                                        return label;
                                    }
                                }
                            }
                        },
                        animation: {
                            onComplete: () => { barRendered = true; checkRendered(); }
                        }
                    }
                }
            );
        }
        if (data.paths) {
            const PathsCanvas = root.querySelector("canvas.ReportCard-path");
            const PathsContext = PathsCanvas.getContext("2d");
            const MaduraiGradient = PathsContext.createLinearGradient(0, 0, 0, 500);
            MaduraiGradient.addColorStop(0, 'rgba(6, 89, 96, 0.5)');
            MaduraiGradient.addColorStop(1, 'rgba(6, 89, 96, 0)');
            const VazarinGradient = PathsContext.createLinearGradient(0, 0, 0, 500);
            VazarinGradient.addColorStop(0, 'rgba(155, 53, 56, 0.5)');
            VazarinGradient.addColorStop(1, 'rgba(155, 53, 56, 0)');
            const NaramonGradient = PathsContext.createLinearGradient(0, 0, 0, 500);
            NaramonGradient.addColorStop(0, 'rgba(175, 119, 91, 0.5)');
            NaramonGradient.addColorStop(1, 'rgba(175, 119, 91, 0)');
            const UnairuGradient = PathsContext.createLinearGradient(0, 0, 0, 500);
            UnairuGradient.addColorStop(0, 'rgba(26, 62, 150, 0.5)');
            UnairuGradient.addColorStop(1, 'rgba(26, 62, 150, 0)');
            const ZenurikGradient = PathsContext.createLinearGradient(0, 0, 0, 500);
            ZenurikGradient.addColorStop(0, 'rgba(114, 75, 127, 0.5)');
            ZenurikGradient.addColorStop(1, 'rgba(114, 75, 127, 0)');
            data.paths.datasets[0].pointBackgroundColor = 'rgba(6, 89, 96, 1)';
            data.paths.datasets[0].borderColor = 'rgba(6, 89, 96, 1)';
            data.paths.datasets[0].fill = 'origin';
            data.paths.datasets[0].backgroundColor = MaduraiGradient;
            data.paths.datasets[1].pointBackgroundColor = 'rgba(155, 53, 56, 1)';
            data.paths.datasets[1].borderColor = 'rgba(155, 53, 56, 1)';
            data.paths.datasets[1].fill = 'origin';
            data.paths.datasets[1].backgroundColor = VazarinGradient;
            data.paths.datasets[2].pointBackgroundColor = 'rgba(175, 119, 91, 1)';
            data.paths.datasets[2].borderColor = 'rgba(175, 119, 91, 1)';
            data.paths.datasets[2].fill = 'origin';
            data.paths.datasets[2].backgroundColor = NaramonGradient;
            data.paths.datasets[3].pointBackgroundColor = 'rgba(26, 62, 150, 1)';
            data.paths.datasets[3].borderColor = 'rgba(26, 62, 150, 1)';
            data.paths.datasets[3].fill = 'origin';
            data.paths.datasets[3].backgroundColor = UnairuGradient;
            data.paths.datasets[4].pointBackgroundColor = 'rgba(114, 75, 127, 1)';
            data.paths.datasets[4].borderColor = 'rgba(114, 75, 127, 1)';
            data.paths.datasets[4].fill = 'origin';
            data.paths.datasets[4].backgroundColor = ZenurikGradient;
            new Chart(
                PathsCanvas,
                {
                    type: 'line',
                    data: data.paths,
                    options: {
                        scales: {
                            x: {
                                title: {
                                    display: true,
                                    text: data.choice
                                },
                                grid: {
                                    color: 'rgba(255,255,255,.1)',
                                },
                                border: {
                                    dash: [2, 4],
                                }
                            },
                            y: {
                                title: {
                                    display: true,
                                    text: data.popularity
                                },
                                beginAtZero: true,
                                ticks: {
                                    callback: (value, index, ticks) => {
                                        return (100.0 * value).toFixed(0) + '%';
                                    }
                                },
                                grid: {
                                    color: 'rgba(255,255,255,.1)',
                                },
                                border: {
                                    dash: [2, 4],
                                }
                            },
                        },
                        plugins: {
                            legend: {
                                position: 'right',
                                labels: {
                                    boxWidth: 16
                                }
                            },
                            tooltip: {
                                callbacks: {
                                    title: (context) => {
                                        return data.choices[context[0].datasetIndex + 1][context[0].dataIndex];
                                    },
                                    label: (context) => {
                                        let label = context.dataset.label || '';
                                        if (label) label += ': ';
                                        label += (100.0 * context.parsed.y).toFixed(1) + '%';
                                        return label;
                                    }
                                }
                            }
                        },
                        animation: {
                            onComplete: () => { linesRendered = true; checkRendered(); }
                        }
                    }
                }
            );
        }
        if (data.schools) {
            const SchoolsCanvas = root.querySelector("canvas.ReportCardSchools-chart");
            //const SchoolsContext = SchoolsCanvas.getContext("2d");
            data.schools.datasets[0].backgroundColor = [
                'rgba(6, 89, 96, 0.5)',
                'rgba(155, 53, 56, 0.5)',
                'rgba(175, 119, 91, 0.5)',
                'rgba(26, 62, 150, 0.5)',
                'rgba(141, 102, 154, 0.5)',
            ];
            data.schools.datasets[0].borderColor = [
                'rgba(6, 89, 96, 1)',
                'rgba(155, 53, 56, 1)',
                'rgba(175, 119, 91, 1)',
                'rgba(26, 62, 150, 1)',
                'rgba(141, 102, 154, 1)',
            ];
            data.schools.datasets[0].borderWidth = 3;
            new Chart(
                SchoolsCanvas,
                {
                    type: 'doughnut',
                    data: data.schools,
                    options: {
                        plugins: {
                            legend: {
                                position: 'right',
                                labels: {
                                    boxWidth: 16
                                }
                            },
                            tooltip: {
                                callbacks: {
                                    label: (context) => {
                                        let label = context.dataset.label || '';
                                        if (label) label += ': ';
                                        label += (100.0 * context.parsed).toFixed(1) + '%';
                                        return label;
                                    }
                                }
                            },
                        },
                        animation: {
                            onComplete: () => { donutRendered = true; checkRendered(); }
                        }
                    }
                }
            );
        }
    }
    const reset = root.querySelector('#score-retry');
    if (reset) {
        reset.addEventListener('mouseover', () => {
            tryPlayAudio('hover', true);
        });
        reset.addEventListener('click', (e) => {
            e.stopPropagation();
            e.preventDefault();
            interacted = true;

            if (lockUI) return;

            lockUI = true;

            tryPlayAudio('click');

            setTimeout(() => {
                resetSim();
            }, 1000);
        });
    }
    const footer = root.querySelector('.ReportFooter');
    if (footer && checkFlags() && data.finale) {
        footer.classList.add('is-truth');

        if (!glitchElements['finale']) {
            const pageElement = document.createElement('div');
            pageElement.classList.add('SimulatorGlitchPage')

            pageElement.innerHTML = data.finale;

            const back = pageElement.querySelector('#finale-back');
            if (back) {
                back.addEventListener('click', (e) => {
                    e.stopPropagation();
                    e.preventDefault();

                    if (lockUI) return;
                    lockUI = true;

                    //tryPlayAudio('click');
                    hideGlitchPage();
                    showPage(700);
                });
            }
            glitchElements['finale'] = {
                back: 700,
                element: pageElement,
            }
            pageAudio['o065c91e9648bf'] = new Audio(MediaRoot+'o065c91e9648bf.mp3');
            SimulatorContentElement.appendChild(pageElement);
        }

        const truth = root.querySelector('#score-truth');
        if (truth) {
            truth.addEventListener('mouseover', () => {
                tryPlayAudio('hover', true);
            });
            truth.addEventListener('click', (e) => {
                e.stopPropagation();
                e.preventDefault();
                interacted = true;

                if (lockUI) return;
                lockUI = true;

                tryPlayAudio('click');
                setStage(700, stateFlags);

                setTimeout(() => {
                    showGlitchPage('finale', 'ambient-report');
                    const header = document.querySelector('.FinaleHeader');
                    let y = 0;
                    if (header) {
                        y = header.getBoundingClientRect().top - SimulatorRoot.getBoundingClientRect().top;
                    }
                    window.scrollTo(0, y);
                    playAudio(pageAudio['o065c91e9648bf']);
                    lockUI = false;
                }, 1000);
            });
        }
    }

    let downloadInitialized = false;
    let barRendered = false;
    let linesRendered = false;
    let donutRendered = false;

    function checkRendered() {
        if (!downloadInitialized && barRendered && linesRendered && donutRendered) {
            downloadInitialized = true;
            initializeDownload(root);
        }
    }
}
async function initializeDownload(rootElement) {
    try {
        const cardElement = rootElement.querySelector('.ReportCard-container');
        const dataUrl = await generateImage(cardElement);

        const download = rootElement.querySelector('#score-download');
        if (download) {
            download.addEventListener('mouseover', () => {
                tryPlayAudio('hover', true);
            });
            download.addEventListener('click', (e) => {
                e.stopPropagation();
                e.preventDefault();
                interacted = true;

                tryPlayAudio('click');

                DownloadJS(dataUrl, 'history-of-tau-summary.jpg', 'image/jpeg');
            });

            console.log('Downloadable report card generated!');
        }
    } catch (error) {
        console.error(`Failed to generate downloadable report card.`, error);
    }
}
async function generateImage(element) {
    try {
        const canvas = await html2canvas(element, {
            backgroundColor: null,
            useCORS: true,
            allowTaint: true,
            scale: 1
        });
        return canvas.toDataURL('image/jpeg', 0.9 );
    } catch (error) {
        console.error('Error generating image:', error);
    }
}

function createGlitchPage(back, data) {
    let page = glitchElements[data.index];
    if (page) {
        page.back = back;
    } else {
        const pageData = {
            back
        };
        if (data.audio) {
            pageAudio[data.audio] = new Audio(MediaRoot+data.audio+'.mp3');
            pageData.audio = data.audio;
        }

        const pageElement = document.createElement('div');
        pageElement.classList.add('SimulatorGlitchPage')

        const imgContainer = document.createElement('div');
        imgContainer.classList.add('SimulatorGlitchPage-wrapper');
        pageElement.appendChild(imgContainer);

        const glitchImg = document.createElement('img');
        glitchImg.classList.add('SimulatorGlitchPage-image');
        glitchImg.src = ImageRoot + data.image;
        glitchImg.alt = '';
        glitchImg.addEventListener('click', () => {
            const value = data.index;
            trySetFlag(value);
        });

        imgContainer.appendChild(glitchImg);

        const content = document.createElement('div');
        content.classList.add('SimulatorGlitchPage-content');
        pageElement.appendChild(content);

        const text = document.createElement('p');
        text.classList.add('SimulatorGlitchPage-text');
        text.innerText = data.text;
        content.appendChild(text);

        const button = document.createElement('div');
        button.classList.add('Button');
        button.classList.add('SimulatorGlitchPage-button');
        button.innerText = PageButtons.back;
        button.addEventListener('click', () => {
            showPage(pageData.back);
        });
        content.appendChild(button);

        pageData.element = pageElement;
        glitchElements[data.index] = pageData;

        SimulatorContentElement.appendChild(pageElement);
    }
}
function showGlitchPage(index, ambient='glitch') {
    SimulatorHeader.classList.add('is-off');
    const pageData = glitchElements[index];
    if (pageData) {
        hideCurrentPage();
        pageData.element.classList.add(CLASS_ACTIVE);
        currentGlitchPage = pageData;
        playAmbient(ambient);
        if (pageData.audio && pageAudio[pageData.audio]) {
            playAudio(pageAudio[pageData.audio]);
        }
    }
}
function hideGlitchPage() {
    if (currentGlitchPage) {
        currentGlitchPage.element.classList.remove(CLASS_ACTIVE);
        currentGlitchPage = null;
    }
    SimulatorHeader.classList.remove('is-off');
}

const PageMute = document.querySelector('.PageMute');
if (PageMute) {
    PageMute.addEventListener('click', (e) => {
        e.stopPropagation();
        e.preventDefault();
        if (allowAudio) {
            PageMute.classList.add(CLASS_ACTIVE);
            muteAudio();
        } else {
            PageMute.classList.remove(CLASS_ACTIVE);
            unmuteAudio();
        }
    });
}

const initialTimestamp = Date.now();
const LOAD_DELAY = 1000;
const LOAD_LOGO_DELAY = 2000;
const LOAD_ANIMATION = 4000;
const SOUND_INTERVAL= 50;
const SOUND_TICKS = 1000 / SOUND_INTERVAL;

document.addEventListener('click', () => {
    const first = !interacted;
    interacted = true;

    if (first) playCurrentAudio();
    else if (interactionCallback) {
        interactionCallback();
    }
});
window.addEventListener('load', () => {
    const elapsed = Date.now() - initialTimestamp;
    if (elapsed > LOAD_DELAY) {
        initAnimation();
    } else {
        setTimeout(initAnimation, LOAD_DELAY - elapsed);
    }

    pageAudio['hover'] = new Audio(MediaRoot+'ui-hover.mp3');
    pageAudio['click'] = new Audio(MediaRoot+'ui-click.mp3');
    pageAudio['glitch'] = new Audio(MediaRoot+'ui-glitch.mp3');
    pageAudio['frame1'] = new Audio(MediaRoot+'warframe-rhino.mp3');
    pageAudio['frame2'] = new Audio(MediaRoot+'warframe-ivara.mp3');
    pageAudio['frame3'] = new Audio(MediaRoot+'warframe-wisp.mp3');
    pageAudio['frame4'] = new Audio(MediaRoot+'warframe-khora.mp3');
    pageAudio['frame5'] = new Audio(MediaRoot+'warframe-nekros.mp3');
    pageAmbient['default'] = new Audio(MediaRoot+'ambient-default.mp3');
    pageAmbient['default'].loop = true;
    pageAmbient['default'].volume = 0;
    pageAmbient['glitch'] = new Audio(MediaRoot+'ambient-glitch.mp3');
    pageAmbient['glitch'].loop = true;
    pageAmbient['glitch'].volume = 0;

    setInterval(audioInterval, SOUND_INTERVAL);
});

const AMBIENT_VOLUME = 0.2;
const AMBIENT_INCREMENT = 0.005;
function audioInterval() {
    if (!interacted || !allowAudio) return;

    if (previousAmbientTrack != null && !previousAmbientTrack.paused) {
        if (previousAmbientTrack > 0) {
            previousAmbientTrack.volume = Math.max(0, previousAmbientTrack.volume - AMBIENT_INCREMENT);
        } else {
            try {
                previousAmbientTrack.pause();
                previousAmbientTrack = null;
            } catch (e) {
                // do nothing
            }
        }
    }
    if (currentAmbientTrack != null) {
        if (currentAmbientTrack.paused) {
            try {
                currentAmbientTrack.volume = 0;
                currentAmbientTrack.play();
            } catch (e) {
                // Do nothing
            }
        }
        if (currentAmbientTrack.volume < AMBIENT_VOLUME) {
            if (ambientDelayTicks > 0) {
                ambientDelayTicks --;
            } else {
                currentAmbientTrack.volume = Math.min(AMBIENT_VOLUME, currentAmbientTrack.volume + AMBIENT_INCREMENT);
            }
        }
    }
}

function initAnimation() {
    SimulatorBackgroundLines.classList.add(CLASS_ACTIVE);
    // Skip loading animation if we're not on the first page (initial state)
    if (stateData.page > 1) {
        initSimulator();
    } else {
        SimulatorBackgroundLines.classList.add('is-animating');
        gsap.from(
            '.SimulatorBackground-lines path',
            {
                duration: 3,
                stagger: 0.08,
                drawSVG: 0
            }
        );
        setTimeout(() => {
            SimulatorLogo.setAttribute('data-state', '0');
            gsap.from(
                '#simulator-logo-path, #simulator-logo-filter',
                {
                    duration: 5,
                    stagger: 0.1,
                    drawSVG: 0
                }
            );
        }, LOAD_LOGO_DELAY);
        setTimeout(initSimulator, LOAD_ANIMATION);
    }
}
function initSimulator() {
    SimulatorRoot.classList.add(CLASS_ACTIVE);
    SimulatorBackgroundGlow.classList.add(CLASS_ACTIVE);
    if (IsSignedIn) {
        showCurrentPage();

        const resetButton = document.querySelector('#sim-reset');
        if (resetButton) {
            resetButton.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                resetSim();
                interacted = true;
            });
        }
        updateDebugText();
    } else {
        SimulatorLogo.setAttribute('data-state', '1');
        document.querySelector('.LoginPrompt').classList.add(CLASS_ACTIVE);
    }
}

const locale = document.querySelector('.PageLocaleSelector');
if (locale) {
    locale.addEventListener('click', (e) => {
        e.stopPropagation();
    });
    const toggle = locale.querySelector('.PageLocaleSelector-language');
    toggle.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        locale.classList.toggle('is-open');
    });
}
